/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * @file mixin for interacting with the toolbar in trix editor
 *
 * The component using this mixin must use RichTextEditor from vuecomponents/rich_text_editor.vue with these props
 * <rich-text-editor
 *  ref="editor"
 *  :toolbar-id="toolbarId"
 *    ...
 * />
 *
 * computed values/methods and data to override:
 * - isToolbarFloating
 * - invalidUrlMessage
 */

import device from 'bits/device'
import { __ } from 'bits/intl'
import { $ } from 'bits/jquery_ui_position'
import { isUrl } from 'bits/url'
import uniqueId from 'lodash.uniqueid'
import Vue from 'vue'
import { Component } from 'vue-property-decorator'

@Component({})
export default class TrixToolbarMixin extends Vue {
  $refs!: {
    editor: any
  }
  toolbarElement: any = null
  editorElement: any = null
  toolbarInputFocused = false
  textLinkValue = ''
  invalidUrlMessage = __('Please enter a valid URL.')
  dir: any

  get isToolbarFloating(): boolean {
    // Implement your own
    return true
  }

  get toolbarId(): string {
    return uniqueId('trixtoolbar')
  }

  repositionTrixToolbar(): void {
    const editor = this.$refs.editor.editor
    if (!editor) {
      this.toolbarElement.hide()
      return
    }

    const range = editor.getSelectedRange()
    if (range[0] === range[1]) {
      this.toolbarElement.hide()
      return
    }

    const selectionStartPosition = editor.getClientRectAtPosition(range[0])
    const selectionEndPosition = editor.getClientRectAtPosition(range[1]) || selectionStartPosition

    // for some languages (e.g. Korean, Japanese), 1 character on screen may consists of multiple keystrokes
    // due to how keyboards in those languages work, they are "selected" to be grouped together
    // when spacebar/backspace is pressed, the temporary "selected" characters are removed and the "real" characters are put in their place
    // => selection position becomes undefined because the original characters disappeared
    if (selectionStartPosition === undefined || selectionEndPosition === undefined) {
      return
    }

    const virtualRectangle = {
      top: selectionStartPosition.top,
      left: Math.min(selectionStartPosition.left, selectionEndPosition.left),
      bottom: selectionEndPosition.top,
      right: Math.max(selectionStartPosition.left, selectionEndPosition.left),
    }

    const $postElement = $(this.$parent.$el)
    const isSelectionStartAndEndDifferent = virtualRectangle.top !== virtualRectangle.bottom
    const anchorX = isSelectionStartAndEndDifferent
      ? $postElement.offset().left + $postElement.width() / 2
      : (virtualRectangle.left + virtualRectangle.right) / 2

    const toolbarTargetFakeEvent = {
      preventDefault: true,
      pageX: anchorX,
      pageY: Math.max(virtualRectangle.top - 10, 0),
    }

    const isAndroid = device.android
    if (isAndroid) {
      this.toolbarElement.show().position({
        my: 'center bottom',
        at: 'center bottom',
        of: $(document),
        collision: 'fit flip',
      })
    } else {
      this.toolbarElement.show().position({
        my: 'center bottom',
        at: 'center top',
        of: toolbarTargetFakeEvent,
        collision: 'fit flip',
      })
    }
  }

  addCustomButtonsToToolbar(): void {
    // subscript and superscript
    const toolbarDomElement = this.toolbarElement[0]

    let buttonHTML =
      '<button type="button" class="trix-button trix-button--icon trix-button--icon-mark" data-trix-attribute="mark" title="Highlight">Highlight</button>'
    buttonHTML +=
      '<button type="button" class="trix-button trix-button--icon trix-button--icon-sup" data-trix-attribute="sup" title="Superscript">Superscript</button>'
    buttonHTML +=
      '<button type="button" class="trix-button trix-button--icon trix-button--icon-sub" data-trix-attribute="sub" title="Subscript">Subscript</button>'

    const buttonGroup = toolbarDomElement.querySelector('.trix-button-group--text-tools')
    buttonGroup.insertAdjacentHTML('beforeend', buttonHTML)

    // separator div
    buttonGroup.insertAdjacentHTML('afterend', '<div class="trix-toolbar-custom-separator" />')

    // math
    buttonHTML =
      '<button type="button" class="trix-button trix-button--icon trix-button--icon-math" data-trix-attribute="math" title="Math">Math</button>'
    const codeButton = toolbarDomElement.querySelector('button[data-trix-attribute="code"]')
    codeButton.insertAdjacentHTML('afterend', buttonHTML)

    toolbarDomElement.classList.add('is-customized')

    // if the element using this mixin has dir set, use it for the toolbar
    if (this.dir) {
      toolbarDomElement.setAttribute('dir', this.dir)
    }
  }

  showTrixToolbar(): void {
    this.toolbarElement.show()
  }

  hideTrixToolbar(): void {
    this.toolbarElement.hide()
  }

  beforeMount(): void {
    // Move the editor to the body to allow us to control its positioning
    const toolbarClass = this.isToolbarFloating ? 'toolbar-floating' : 'toolbar-fixed'
    const appendTo = document.body
    this.toolbarElement = $(`<trix-toolbar class="${toolbarClass}" id="${this.toolbarId}"></trix-toolbar>`).appendTo(
      appendTo,
    )
  }

  mounted(): void {
    const toolbarElement = this.toolbarElement
    this.editorElement = this.$refs.editor.$refs.editor
    const $input = toolbarElement.find('input')

    toolbarElement.on('click', function (e) {
      e.stopPropagation()
    })

    // Doing this in defer because some browsers don't have the toolbar fully developed at this point.

    setTimeout(() => {
      this.addCustomButtonsToToolbar()
      const $backButton = $(
        '<button class="trix-back-button icon-button" title="Back to formatting menu" style="font-size: 20px;">back</button>',
      ).prependTo(toolbarElement.find('.trix-dialog__link-fields'))
      $backButton.on('click', () => {
        const linkButtonElement = toolbarElement.find('[data-trix-action=link]')[0]
        const mousedownEvent = new CustomEvent('mousedown', { bubbles: true, cancelable: true })
        linkButtonElement.dispatchEvent(mousedownEvent)
      })
    }, 0)

    // When the url form is open, detect text selection. If the selection is a url, prefill that in the url form
    $input.on('focus', () => {
      const editor = this.editorElement.editor
      this.toolbarInputFocused = true
      if (!this.textLinkValue) {
        const currentText = editor.getDocument().toString()
        const currentSelection = editor.getSelectedRange()
        const textWeAreInterestedIn = currentText.substring(currentSelection[0], currentSelection[1])
        if (isUrl(textWeAreInterestedIn)) {
          this.textLinkValue = textWeAreInterestedIn
        } else {
          const tryUrl = 'http://' + textWeAreInterestedIn
          if (isUrl(tryUrl)) {
            this.textLinkValue = tryUrl
          }
        }
      }
    })

    $input.on('blur', () => {
      this.toolbarInputFocused = false
    })

    // If the url is invalid, check if adding a http:// before the url will make it valid. If it will, then do that,
    // else notify that the url is invalid.
    $input.on('invalid', (e) => {
      const oldValue = e.target.value || this.textLinkValue
      if (oldValue.indexOf('http://') === 0 || oldValue.indexOf('https://') === 0) {
        // reasonably well formed url
        alert(this.invalidUrlMessage)
      } else {
        const newValue = 'https://' + oldValue
        if (isUrl(newValue)) {
          this.$nextTick(() => {
            this.textLinkValue = newValue
            e.target.value = newValue

            toolbarElement.find('input[data-trix-method=setAttribute]').click()
          })
        } else {
          alert(this.invalidUrlMessage)
        }
      }
    })
  }
}
